import { UpdateAssignment } from './../models/Assignment';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';

import { AssignmentApi, Assignment, NewAssignment } from '../models';
import { AssignmentType } from '../models/Assignment';
import { mapAssignmentApiToAssignment } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';

import { API_FLEET_URL } from '@/utils';

interface AssignmentsFilter {
  dateFrom?: string;
  dateTo?: string;
  driverId?: string;
  businessId?: string;
  vehicleId?: string;
  vehicleTypeId?: string;
  serviceTypeId?: string;
  status?: string[];
}

const assignmentsService = createApi({
  reducerPath: 'assignmentsService',
  baseQuery: createBaseQueryWithReauth(`${API_FLEET_URL}/assignments`),
  tagTypes: ['Assignment', 'Assignments'],
  endpoints: (builder) => ({
    getAssignment: builder.query<Assignment, { assignmentId: string }>({
      query: ({ assignmentId }) => ({
        url: `/${assignmentId}`,
      }),
      transformResponse: (response: AssignmentApi) =>
        mapAssignmentApiToAssignment(response),
      providesTags: ['Assignment'],
    }),
    getAssignments: builder.query<
      PaginatedResponse<Assignment>,
      {
        type: AssignmentType;
        filters: AssignmentsFilter;
        paginated: PaginatedRequest;
      }
    >({
      query: ({ type, filters, paginated }) => ({
        url: '',
        params: {
          type,
          driverId: filters.driverId || undefined,
          commercialBusinessId: filters.businessId || undefined,
          vehicleId: filters.vehicleId || undefined,
          vehicleTypeId: filters.vehicleTypeId || undefined,
          serviceTypeId: filters.serviceTypeId || undefined,
          scheduledFrom: filters.dateFrom,
          scheduledTo: filters.dateTo,
          status: filters.status,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<AssignmentApi>) => ({
        ...response,
        items: response.items.map(mapAssignmentApiToAssignment),
      }),
      providesTags: ['Assignments'],
    }),
    getAssignmentsByDate: builder.query<
      PaginatedResponse<Assignment>,
      {
        type: AssignmentType;
        filters: AssignmentsFilter;
        paginated: PaginatedRequest;
      }
    >({
      query: ({ type, filters, paginated }) => ({
        url: '/scheduled',
        params: {
          type,
          driverId: filters.driverId || undefined,
          commercialBusinessId: filters.businessId || undefined,
          vehicleId: filters.vehicleId || undefined,
          from: filters.dateFrom,
          to: filters.dateTo,
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<AssignmentApi>) => ({
        ...response,
        items: response.items.map(mapAssignmentApiToAssignment),
      }),
      providesTags: ['Assignments'],
    }),
    createAssignment: builder.mutation<Assignment, NewAssignment>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Assignments'],
    }),
    updateAssignment: builder.mutation<
      Assignment,
      { assignmentId: string; data: UpdateAssignment }
    >({
      query: ({ assignmentId, data }) => ({
        url: `/${assignmentId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    deleteAssignment: builder.mutation<void, { assignmentId: string }>({
      query: ({ assignmentId }) => ({
        url: `/${assignmentId}/discard`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    setVehicleToAssignment: builder.mutation<
      Assignment,
      { assignmentId: string; vehicleId: string; force?: boolean }
    >({
      query: ({ assignmentId, vehicleId, force }) => ({
        url: `/${assignmentId}/set-vehicle`,
        method: 'PATCH',
        body: { vehicleId },
        params: { force },
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    setDriverToAssignment: builder.mutation<
      Assignment,
      { assignmentId: string; driverId: string; value_amount: number }
    >({
      query: ({ assignmentId, driverId, value_amount }) => ({
        url: `/${assignmentId}/set-driver`,
        method: 'PATCH',
        body: { driverId, value_amount },
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    setStartWorkToAssignment: builder.mutation<
      Assignment,
      { assignmentId: string; startWork?: string }
    >({
      query: ({ assignmentId, startWork }) => ({
        url: `/${assignmentId}/set-start-work`,
        method: 'PATCH',
        body: { startWork },
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    setEndWorkToAssignment: builder.mutation<
      Assignment,
      { assignmentId: string; endWork?: string }
    >({
      query: ({ assignmentId, endWork }) => ({
        url: `/${assignmentId}/set-end-work`,
        method: 'PATCH',
        body: { endWork },
      }),
      invalidatesTags: ['Assignment', 'Assignments'],
    }),
    createCalendarAssignment: builder.mutation<
      Assignment,
      { templateId: string; repeat_until: string } & Partial<
        Record<
          | 'monday'
          | 'tuesday'
          | 'wednesday'
          | 'thursday'
          | 'friday'
          | 'saturday'
          | 'sunday',
          boolean
        >
      >
    >({
      query: ({ templateId, repeat_until, ...days }) => ({
        url: '/calendar',
        method: 'POST',
        body: {
          templateId,
          repeat_until,
          ...days,
        },
      }),
      invalidatesTags: ['Assignments'],
    }),
  }),
});

export default assignmentsService;

export const {
  useGetAssignmentQuery,
  useGetAssignmentsQuery,
  useGetAssignmentsByDateQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  useSetVehicleToAssignmentMutation,
  useSetStartWorkToAssignmentMutation,
  useSetEndWorkToAssignmentMutation,
  useSetDriverToAssignmentMutation,
  useCreateCalendarAssignmentMutation,
} = assignmentsService;
